import React, { useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowRight, Clock, Calendar, Check, ChevronDown, Wrench } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Seo from "@/components/Seo";
import Breadcrumbs, { toLdItems } from "@/components/Breadcrumbs";
import NotFound from "@/pages/NotFound";
import { SITE_URL, SERVICES, buildBreadcrumbLd, buildFaqLd } from "@/lib/seo";
import { getGuide, GUIDES, GUIDE_CATEGORIES, readingTime } from "@/data/guides";
import { useSeoT, GUIDES_I18N } from "@/lib/seoPagesI18n";

const fmtDate = (d) => new Date(d).toLocaleDateString("fr-CH", { day: "numeric", month: "long", year: "numeric" });

export default function GuideArticle() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { lang, t: st } = useSeoT();
  const [openIdx, setOpenIdx] = useState(-1);
  const base = getGuide(slug);
  if (!base) return <NotFound />;
  const g = lang !== "fr" ? { ...base, ...(GUIDES_I18N[lang]?.[slug] || {}) } : base;

  const path = `/guides/${g.slug}`;
  const bc = [
    { name: "Accueil", path: "/" },
    { name: "Guides", path: "/guides" },
    { name: g.title, path },
  ];
  const jsonLd = [
    {
      "@context": "https://schema.org", "@type": "Article",
      headline: g.h1, description: g.metaDescription,
      datePublished: g.date, dateModified: g.updatedAt || g.date,
      author: { "@type": "Organization", name: "Mopper" },
      publisher: { "@id": `${SITE_URL}/#organization` },
      mainEntityOfPage: `${SITE_URL}${path}`,
    },
    buildBreadcrumbLd(toLdItems(bc)),
  ];
  if (g.faq && g.faq.length) jsonLd.push(buildFaqLd(g.faq));

  const relGuides = (g.related?.guides || []).map(getGuide).filter(Boolean);
  const relServices = (g.related?.services || []).map((s) => SERVICES.find((x) => x.slug === s)).filter(Boolean);
  const catLabel = st.guides.cats?.[g.category] || GUIDE_CATEGORIES.find((c) => c.id === g.category)?.label;

  return (
    <div className="min-h-screen bg-white text-[#0A0F2C]" data-testid={`guide-${g.slug}`}>
      <Seo title={`${g.title} | Mopper`} description={g.metaDescription} path={path} jsonLd={jsonLd} />
      <Header />
      <article>
        <header className={`relative overflow-hidden pt-28 md:pt-36 pb-12 md:pb-16 px-5 sm:px-8 md:px-10 lg:px-14 ${g.heroTone === "dark" ? "bg-[#0A0F2C]" : "bg-[#2D5BFF]"} text-white`}>
          <div className="pointer-events-none absolute inset-0" style={{ background: "radial-gradient(ellipse at top right, rgba(255,255,255,0.14), transparent 55%)" }} />
          <div className="relative mx-auto" style={{ maxWidth: "820px" }}>
            <div className="mb-6"><Breadcrumbs items={bc} variant="dark" /></div>
            <span className="inline-block rounded-full bg-white/10 border border-white/15 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-[#FFD84D]">{catLabel}</span>
            <h1 className="mt-4 font-extrabold text-3xl md:text-4xl lg:text-5xl tracking-tight leading-[1.05]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{g.h1}</h1>
            <div className="mt-5 flex flex-wrap items-center gap-4 text-sm text-white/80">
              <span className="inline-flex items-center gap-1.5"><Calendar size={14} /> {fmtDate(g.date)}</span>
              <span className="inline-flex items-center gap-1.5"><Clock size={14} /> {readingTime(g)} {st.guides.readingSuffix}</span>
            </div>
          </div>
        </header>

        <div className="mx-auto px-5 sm:px-8 md:px-10 lg:px-14 py-12 md:py-16" style={{ maxWidth: "820px" }}>
          <p className="text-lg text-[#4B5563] leading-relaxed">{g.excerpt}</p>
          {g.sections.map((s, i) => (
            <section key={i} className="mt-10" data-testid={`guide-section-${i}`}>
              <h2 className="font-extrabold text-2xl tracking-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{s.h2}</h2>
              {(s.p || []).map((para, j) => (<p key={j} className="mt-4 text-[#374151] leading-relaxed text-[17px]">{para}</p>))}
              {s.list && (
                <ul className="mt-4 flex flex-col gap-2.5">
                  {s.list.map((li, k) => (
                    <li key={k} className="flex items-start gap-2.5 text-[17px] text-[#374151]"><span className="mt-1 inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#2D5BFF] text-white shrink-0"><Check size={11} strokeWidth={3} /></span><span>{li}</span></li>
                  ))}
                </ul>
              )}
            </section>
          ))}

          {/* FAQ */}
          {g.faq && g.faq.length > 0 && (
            <section className="mt-12">
              <h2 className="font-extrabold text-2xl tracking-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{st.guides.faqTitle}</h2>
              <div className="mt-5 flex flex-col gap-3">
                {g.faq.map((it, i) => {
                  const open = openIdx === i;
                  return (
                    <div key={i} className="bg-[#F7F7F8] rounded-2xl overflow-hidden" data-testid={`guide-faq-${i}`}>
                      <button className="w-full text-left p-5 flex items-center justify-between gap-4" onClick={() => setOpenIdx(open ? -1 : i)} aria-expanded={open}>
                        <span className="font-bold">{it.q}</span>
                        <ChevronDown size={18} className={`shrink-0 text-[#2D5BFF] transition-transform ${open ? "rotate-180" : ""}`} />
                      </button>
                      {open && <div className="px-5 pb-5 text-[#4B5563] leading-relaxed">{it.a}</div>}
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          {/* CTA + maillage */}
          <div className="mt-12 rounded-3xl bg-[#0A0F2C] text-white p-8 text-center">
            <h2 className="font-extrabold text-2xl md:text-3xl tracking-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{st.guides.ctaTitle}</h2>
            <p className="mt-3 text-white/80">{st.guides.ctaText}</p>
            <button onClick={() => navigate("/devis")} className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#FFD84D] hover:bg-yellow-300 text-[#0A0F2C] px-7 py-4 font-extrabold text-sm transition-all" data-testid="guide-cta-quote">
              {st.guides.ctaBtn} <ArrowRight size={16} />
            </button>
          </div>

          {(relServices.length > 0 || (g.related?.tools || []).length > 0 || relGuides.length > 0) && (
            <div className="mt-10 border-t border-gray-100 pt-8 flex flex-col gap-3">
              {relServices.map((s) => (
                <Link key={s.slug} to={`/services/${s.slug}`} className="inline-flex items-center gap-2 font-semibold hover:text-[#2D5BFF]" data-testid={`guide-rel-service-${s.slug}`}>{st.guides.relService(s.name)} <ArrowRight size={14} /></Link>
              ))}
              {(g.related?.tools || []).map((tl) => (
                <Link key={tl} to={`/outils/${tl}`} className="inline-flex items-center gap-2 font-semibold hover:text-[#2D5BFF]" data-testid={`guide-rel-tool-${tl}`}><Wrench size={14} /> {st.guides.relTool} <ArrowRight size={14} /></Link>
              ))}
              {relGuides.map((rg) => (
                <Link key={rg.slug} to={`/guides/${rg.slug}`} className="inline-flex items-center gap-2 font-semibold hover:text-[#2D5BFF]" data-testid={`guide-rel-guide-${rg.slug}`}>{st.guides.relGuide(rg.title)} <ArrowRight size={14} /></Link>
              ))}
            </div>
          )}
        </div>
      </article>
      <Footer />
    </div>
  );
}
